import React from "react";
import { IStoreState, useAppDispatch, useAppSelector } from "../../../redux";
import { IBreadcrumbProps } from "../../../layout/breadcrumb/IBreadcrumbProps";
import { useNavigate, useParams } from "react-router-dom";
import { ISelectOption } from "../../../constants/types";
import { useFormik } from "formik";
import {
  fetchSingleTemplateAsync,
  fetchTemplateSQLViewAndColumnsAsync,
  upsertSingleTemplateAsync,
} from "../../../redux/settings/templates";
import { PageLoader } from "../../../Components/PageLoader/PageLoader";
import { Breadcrumb } from "../../../layout/breadcrumb/Breadcrumb";
import {
  Box,
  Grid,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { StandardCard } from "../../../Components/Card";
import {
  ControlledCustomSelect,
  CustomFormLabel,
  CustomTextField,
} from "../../../Components/formComponents";
import { ContentCopyOutlined } from "@mui/icons-material";
import { RoleBasedCustomButton } from "../../../security/RoleBasedComponents/RoleBasedCustomButton/RoleBasedCustomButton";
import { ACCESSIBLE_MUDULE_KEYS, LoadState } from "../../../constants/enum";
import { CustomAutoComplete } from "../../../Components/formComponents/CustomAutoComplete";
import { fetchTableOrColumnNamesAsync } from "../../../redux/general/general.actions";
import { appNabarRoutes } from "../../../router/AppNavbarRoutes";
import {
  displaySnackBarDialog,
  openLoaderWithMessage,
} from "../../../redux/common";
import { Editor } from "../../../Components/Editor/Editor";

export const SingleTemplateForm = () => {
  const { templateCode } = useParams() as { templateCode?: any };

  const BCrumb: IBreadcrumbProps["items"] = [
    {
      to: "/dashboard",
      title: "dashboard",
    },
    {
      to: "/templates",
      title: "templates",
    },
    {
      title: templateCode ? "Edit Template" : "Create Template",
    },
  ];

  // eslint-disable-next-line react-hooks/rules-of-hooks
  const {
    data: singleDataObject,
    loading,
    error,
  } = useAppSelector(
    (storeState: IStoreState) => storeState.settings.templates.single_template
  );
  const dispatch = useAppDispatch();
  const [saveLoading, setSaveLoading] = React.useState(false);

  const [tables, setTables] = React.useState<ISelectOption[]>([]);
  const [columnNames, setColumnNames] = React.useState<ISelectOption[]>([]);
  const [columnsLoading, setColumnsLoading] = React.useState(false);
  const [tableError, setTableError] = React.useState<string | null>(null);
  const [field, setField] = React.useState<string | null>(null);
  const [trackChanges, setTrackChanges] = React.useState<string | null>(null);

  // const [dropdownList1, setDropdownList1] = React.useState<any>([]);
  const navigate = useNavigate();

  const {
    values,
    errors,
    setFieldValue,
    setValues,
    handleSubmit,
    handleChange,
  } = useFormik({
    initialValues: singleDataObject,
    validate: (values) => {
      const errors: any = {};
      if (!values.template_name) {
        errors.template_name = "Template name is required.";
      }
      if (!values.template_category) {
        errors.template_category = "Template category is required";
      }
      if (!values.template_subject) {
        errors.template_subject = "Subject is required.";
      }
      // if (values.table_name && !values.column) {
      //   errors.column = "Column is required.";
      // }
      return errors;
    },
    onSubmit: (values) => {
      setSaveLoading(true);
      dispatch(upsertSingleTemplateAsync(values))
        .then((res) => {
          if (res.meta.requestStatus === "fulfilled") {
            navigate(appNabarRoutes.settings.template);
          }
        })
        .finally(() => {
          setSaveLoading(false);
        });
    },
  });

  const copyToClipboard = (columnName: string) => async () => {
    if (values.table_name_or_dynamic_view_code) {
      const tableName = values.table_name_or_dynamic_view_code;
      const columnValue = columnName;
      let fieldString = `${columnValue}`;
      if (values.call_type === "TABLE") {
        fieldString = `@@${tableName}.${columnValue}`;
      } else {
        fieldString = `@@${columnValue}`;
      }
      try {
        await navigator.clipboard.writeText(fieldString);
        displaySnackBarDialog({
          type: "success",
          content: "Text copied successfully!",
        });
      } catch (err) {
        console.error("Failed to copy text: ", err);
      }
    }

    // console.log(field)
  };
  const copyToClipboard1 = () => async () => {
    if (trackChanges) {
      try {
        await navigator.clipboard.writeText(trackChanges);
        dispatch(
          displaySnackBarDialog({
            type: "success",
            content: "Text copied successfully!",
          })
        );
      } catch (err) {
        console.error("Failed to copy text: ", err);
      }
    }
  };

  React.useEffect(() => {
    if (templateCode) {
      dispatch(fetchSingleTemplateAsync(templateCode));
    }
  }, [templateCode]);

  React.useEffect(() => {
    setValues(singleDataObject);
  }, [singleDataObject]);

  React.useEffect(() => {
    if (values.track_changes) {
      const track = values.track_changes;
      setTrackChanges(track);
      setFieldValue("track_changes", track);
    }
    // Set the initial value for track_changes
  }, [values.track_changes]);

  React.useEffect(() => {
    setTables([]);
    setColumnNames([]);
    if (values.call_type === "TABLE") {
      dispatch(
        fetchTableOrColumnNamesAsync({
          onCallback: (modules) => {
            setTables(modules);
          },
        })
      );
    } else if (values.call_type === "SQL_VIEW") {
      dispatch(
        fetchTemplateSQLViewAndColumnsAsync({
          onCallback: (modules) => {
            setTables(modules);
          },
        })
      );
    }
  }, [values.call_type]);

  React.useEffect(() => {
    setColumnsLoading(true);

    if (values.call_type === "TABLE") {
      dispatch(
        fetchTableOrColumnNamesAsync({
          onCallback: (modules) => {
            setColumnNames(modules);
            setColumnsLoading(false);
          },
          tableName: values.table_name_or_dynamic_view_code,
        })
      );
    } else if (values.call_type === "SQL_VIEW") {
      dispatch(
        fetchTemplateSQLViewAndColumnsAsync({
          onCallback: (modules) => {
            setColumnNames(modules);
            setColumnsLoading(false);
          },
          tableName: values.table_name_or_dynamic_view_code,
        })
      );
    } else {
      setColumnNames([]);
    }
  }, [values.table_name_or_dynamic_view_code]);

  return (
    <PageLoader
      loading={loading === LoadState.pending}
      error={error ? { message: error } : null}
    >
      <Breadcrumb title="" items={BCrumb} />
      <form onSubmit={handleSubmit}>
        <Grid container spacing={1}>
          <Grid item xs={12} md={8}>
            <StandardCard
              heading={templateCode ? "Edit Template" : "Create New Template"}
            >
              <Grid container spacing={1}>
                <Grid item xs={12} md={4}>
                  <CustomFormLabel>Template Name</CustomFormLabel>
                  <CustomTextField
                    name="template_name"
                    variant="outlined"
                    size="small"
                    type="text"
                    fullWidth
                    value={values.template_name}
                    error={errors.template_name ? true : false}
                    helperText={errors.template_name}
                    onChange={handleChange}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <CustomFormLabel>Template Category</CustomFormLabel>
                  <ControlledCustomSelect
                    fullWidth
                    value={values.template_category}
                    name="template_category"
                    onChange={handleChange}
                    placeholder="Select one"
                    displayEmpty
                    options={["SMS", "WhatsApp", "Email"].map((template) => {
                      return { label: template, value: template };
                    })}
                    error={errors.template_category}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <CustomFormLabel>Subject</CustomFormLabel>
                  <CustomTextField
                    name="template_subject"
                    variant="outlined"
                    size="small"
                    type="text"
                    fullWidth
                    value={values.template_subject}
                    error={errors.template_subject ? true : false}
                    helperText={errors.template_subject as any}
                    onChange={handleChange}
                  />
                </Grid>

                <Grid item xs={12} md={4}>
                  <CustomFormLabel>Status</CustomFormLabel>
                  <ControlledCustomSelect
                    name="status"
                    variant="outlined"
                    size="small"
                    type="text"
                    fullWidth
                    displayEmpty
                    placeholder="Select One"
                    options={[
                      { label: "Active", value: "ACTIVE" },
                      { label: "Inactive", value: "INACTIVE" },
                    ].map((data) => {
                      return { label: data.label, value: data.value };
                    })}
                    value={values.status}
                    onChange={handleChange}
                  />
                </Grid>
               {/* <Grid item xs={12} md={4}>
                                    <CustomFormLabel>Type</CustomFormLabel>
                                    <ControlledCustomSelect
                                        name="call_type"
                                        variant="outlined"
                                        size="small"
                                        type="text"
                                        fullWidth
                                        displayEmpty
                                        placeholder="Select One"
                                        options={[
                                            { label: "Table", value: "TABLE" },
                                            { label: "Sql View", value: "SQL_VIEW" },
                                        ].map((data) => {
                                            return { label: data.label, value: data.value };
                                        })}
                                        value={values.call_type}
                                        onChange={handleChange}
                                    />
                                </Grid>
                                <Grid item xs={12} md={4}>
                                    <CustomFormLabel>Table Name</CustomFormLabel>
                                    <CustomAutoComplete
                                        value={values.table_name_or_dynamic_view_code}
                                        onChange={(newValue) =>
                                            setFieldValue("table_name_or_dynamic_view_code", newValue)
                                        }
                                        placeholder="Select one"
                                        options={tables.map((table) => {
                                            return {
                                                label: table.label,
                                                value: table.value as string,
                                            };
                                        })}
                                    ></CustomAutoComplete>
                                </Grid> */}

                             {/*     <Grid item xs={12} md={5}>
                                    <CustomFormLabel>
                                        This will show comparing btw old and new value
                                    </CustomFormLabel>
                                    <Box display={"flex"} alignItems={"center"}>
                                        <TextField
                                            name="track_changes"
                                            variant="outlined"
                                            size="small"
                                            value={values.track_changes}
                                            type="text"
                                            fullWidth
                                            sx={{ mr: 1 }}
                                            disabled
                                        ></TextField>
                                        <ContentCopyOutlined
                                            color={"primary"}
                                            sx={{ cursor: "pointer" }}
                                            onClick={copyToClipboard1()}
                                        />
                                    </Box>
                                </Grid> */}

                <Grid item xs={12} mt={3}>
                  <Box
                    sx={{
                      ".ql-container": {
                        minHeight: 200,
                        background: "red",
                      },
                    }}
                  >
                    {/* <ReactQuill
                      modules={modules}
                      formats={formats}
                      theme="snow"
                      value={values.body || ""}
                      onChange={(newValue) => setFieldValue("body", newValue)}
                    /> */}
                    <Editor
                      content={values.body || ""}
                      onChange={(newValue) => setFieldValue("body", newValue)}
                    />
                  </Box>
                </Grid>
              </Grid>
              <Box marginTop={4}>
                <RoleBasedCustomButton
                  variant="outlined"
                  moduleId={ACCESSIBLE_MUDULE_KEYS.CATEGORY}
                  disabled={saveLoading}
                  type="submit"
                >
                  Save Template
                </RoleBasedCustomButton>
              </Box>
            </StandardCard>
          </Grid>
          {/* <Grid item xs={12} lg={4}>
            <Paper variant="outlined">
              <Typography
                variant="body1"
                sx={{ p: 2, pb: 0 }}
                fontSize={"1.1rem"}
                  // color="primary"
                  fontWeight={600}
              >
                Columns Available
              </Typography>

              {!values.table_name_or_dynamic_view_code && (
                <>
                  <Stack
                    p={2}
                    height={200}
                    display={"flex"}
                    justifyContent={"center"}
                    alignItems={"center"}
                  >
                    <Typography
                      variant="body1"
                      fontSize={"1rem"}
                      color="textSecondary"
                    >
                      Please select a table to view its columns.
                    </Typography>
                  </Stack>
                </>
              )}
              {values.table_name_or_dynamic_view_code &&
                !columnsLoading &&
                columnNames.length === 0 && (
                  <>
                    <Stack
                      p={2}
                      height={200}
                      display={"flex"}
                      justifyContent={"center"}
                      alignItems={"center"}
                    >
                      <Typography
                        variant="body1"
                        fontSize={"1rem"}
                        color="textSecondary"
                      >
                        No columns available for the selected table.
                      </Typography>
                    </Stack>
                  </>
                )}
              <Stack maxHeight={"60vh"} overflow={"auto"} sx={{ p: 1 }}>
                {columnNames.map((column, index) => {
                  return (
                    <Stack
                      key={index}
                      direction={"row"}
                      justifyContent={"space-between"}
                      alignItems={"center"}
                      pr={1}
                    >
                      <ListItemButton
                        onClick={copyToClipboard(column.value as string)}
                      >
                        <ListItemText
                          primary={column.label}
                          sx={{
                            "& span": {
                              fontSize: "0.9rem",
                              textOverflow: "ellipsis",
                              //width: "80%",
                              overflow: "hidden",
                              wordWrap: "nowrap",
                            },
                          }}
                        />
                        <ListItemIcon>
                          <ContentCopyOutlined
                            
                            sx={{ cursor: "pointer" }}
                          />
                        </ListItemIcon>
                      </ListItemButton>
                    </Stack>
                  );
                })}
              </Stack>
            </Paper>
          </Grid> */}
        </Grid>
      </form>
    </PageLoader>
  );
};
